import ModernError, {
  type ErrorInstance,
  type InstanceOptions,
} from "modern-errors";
import modernErrorsSerialize from "modern-errors-serialize";
import { dedupeParagraphSentences } from "../string/dedupeParagraphSentences.js";
import { punctuateSentenceWithPeriod } from "../string/punctuateSentenceWithPeriod.js";

/**
 * Key-value variables of contextual information that are passed to the BaseError error explanation and reference URL getter functions
 *
 * @see {@link BaseErrorProps.errorCtx}
 * @see {@link BaseErrorProps.getExplanation}
 * @see {@link BaseErrorProps.getReferenceURL}
 * @see {@link BaseError.getExplanation}
 * @see {@link BaseError.getReferenceURL}
 * @see {@link BaseError.getMergedErrorContext}
 * @see {@link BaseError.getExplanationOfAggregateErrors}
 */
export type ErrorCtxType = Record<
  string,
  string | string[] | number | boolean | Date | undefined | unknown | unknown[]
>;

/**
 * Use this type to define the options parameter for a custom error class extending the ModernError class
 * @see {@link ModernError}
 */
export type CustomErrorClassOptionsType<T extends Record<string, any>> = T &
  InstanceOptions;
/**
 * Use this type to type a custom error class that extends the BaseError class
 * @example CustomErrorClassType<typeof UnknownError>
 * @see {@link BaseError}
 */
export type CustomErrorClassType<T extends typeof BaseError> = ReturnType<
  T["subclass"]
>;
/**
 * Use this to type an instance of a custom error class that extends the BaseError class
 * @example CustomErrorInstanceType<typeof UnknownError>
 * @see {@link BaseError}
 */
export type CustomErrorInstanceType<T extends typeof BaseError> =
  InstanceType<T>;
/**
 * Use this type to define the parameters for the instantiation of a custom error class that extends the BaseError class
 * @example BaseErrorSubClassInstanceProps<HTTPResponseErrorProps>
 * @see {@link BaseError}
 */
export type BaseErrorSubClassInstanceProps<T extends Record<string, any>> = T &
  Pick<ErrorInstance, "cause" | "errors">;
/**
 * Use this type to define the 'props' parameter of a custom error class that extends the BaseError class
 * @example const CustomError = BaseError.subclass("CustomError", { props: {} as BaseErrorSubClassPropsType<{ expected: unknown; actual: unknown }> })
 *
 * new CustomError("Custom error", {
 *   props: { expected: "string", actual: 123 } satisfies BaseErrorSubClassPropsType<{ expected: unknown; actual: unknown }>,
 * })
 * @see {@link BaseError}
 */
export type BaseErrorSubClassPropsType<T> =
  T extends Record<string, unknown> ? T : never;

export interface BaseErrorProps {
  /** the reference code of the error */
  code: number;
  /** the severity of the error (default: "error") */
  severity: "fatal" | "error" | "warning" | "debug";
  /** getter for the user-facing error message. variables passed in are merged with the errorCtx property, with the variables passed in taking precedence */
  getExplanation: <T extends ErrorCtxType = ErrorCtxType>(ctx?: T) => string;
  /** default explanation to use if no explanation getter or error message (e.g. new BaseError("")) is provided. (default: "An unknown error occurred.") */
  defaultExplanation: string;
  /** getter for the link the user can take to understand the error better, if available. variables passed in are merged with the errorCtx property, with the variables passed in taking precedence */
  getReferenceURL: <T extends ErrorCtxType = ErrorCtxType>(ctx?: T) => string;
  /** variables passed to the explanation and reference URL getter functions. this will be overridden by the variables passed in to the getExplanation and getReferenceURL functions when called. */
  errorCtx?: ErrorCtxType;
}

/**
 * Custom error class that enhances error handling across the front-end
 * monorepo. Extend this class to create new error classes for the app or
 * package you need to throw errors in.
 *
 * Every error has an associated error code, a function to get an error
 * explanation, and a reference URL, all intended to be shown to the end-user.
 * The sub-classes can have free-form custom properties, which can be easily
 * accessed and are serialized for reporting in Sentry.
 *
 * @example
 * export const DownloadError = BaseError.subclass("DownloadError", {
 *   props: {
 *     fileName: string;
 *     fileSize: number;
 *     fileType: string;
 *     fileUrl: string;
 *     fileId: string;
 *   }
 * });
 *
 * throw new DownloadError("Download failed", {
 *   fileName: "example.pdf",
 *   fileSize: 1000,
 *   fileType: "application/pdf",
 *   fileUrl: "https://example.com/example.pdf",
 *   fileId: "1234567890",
 * });
 *
 * if (err instanceof BaseError) {
 *   // properties that can be accessed: err.code, err.getExplanation({...}), err.getReferenceURL({...})
 * }
 */
export const BaseError = ModernError.subclass("BaseError", {
  serialize: {
    exclude: [
      "_isValidereDefined",
      "_getExplanation",
      "_getReferenceURL",
      "defaultExplanation",
      "stack",
    ],
  },
  custom: class extends ModernError {
    /** is this a Validere-defined error? @internal */
    readonly _isValidereDefined = true;
    /** @see {@link BaseErrorProps.getExplanation} @internal */
    _getExplanation?: BaseErrorProps["getExplanation"];
    /** @see {@link BaseErrorProps.getReferenceURL} @internal */
    _getReferenceURL?: BaseErrorProps["getReferenceURL"];
    /** Since cause is merged with the error class by modern-errors, we attempt
     * to extract the cause explanation/message for use in the getExplanation()
     * getter. Note, the cause's explanation does not receive the error ctx
     * variables passed to the getExplanation() getter - the parent error
     * classes' ctx during instantiation is passed in as overrides. @internal */
    _causeExplanation?: string;
    /** Since cause is merged with the error class by modern-errors, we attempt
     * to extract the cause name for use in logging. @internal */
    _causeName?: string;
    /** @see {@link BaseErrorProps.code} */
    readonly code: BaseErrorProps["code"];
    /** @see {@link BaseErrorProps.severity} */
    readonly severity: BaseErrorProps["severity"];
    /** @see {@link BaseErrorProps.errorCtx} */
    readonly errorCtx?: BaseErrorProps["errorCtx"] = {};
    /** @see {@link BaseErrorProps.defaultExplanation} */
    readonly defaultExplanation: BaseErrorProps["defaultExplanation"] =
      "An unknown error occurred.";

    constructor(
      message: string,
      options: CustomErrorClassOptionsType<Partial<BaseErrorProps>> = {}
    ) {
      const causeCopy: string | undefined =
        options.cause instanceof BaseError
          ? options.cause?.getExplanation?.(options.errorCtx)
          : options.cause instanceof Error
            ? (options.cause?.message ?? undefined)
            : undefined;
      const causeName =
        options.cause instanceof BaseError || options.cause instanceof Error
          ? options.cause.name
          : undefined;

      super(message, options);

      this._causeExplanation = causeCopy;
      this._causeName = causeName;
      this.code = options.code ?? 0;
      this.severity = options.severity ?? "error";
      this._getExplanation = options.getExplanation;
      this._getReferenceURL = options.getReferenceURL;
      this.errorCtx = options.errorCtx;
      this.defaultExplanation =
        options.defaultExplanation ?? "An unknown error occurred.";
    }

    get nameWithCode(): string {
      return `${this.name}_${this.code}`;
    }

    /**
     * Get the user-facing error message.
     *
     * Note, the string will be sentences punctuated with periods.
     *
     * @param ctx - variables passed to the explanation getter function
     * @param opts - options for constructing the explanation
     * @param opts.showErrorName - whether to show the error name in the explanation (default: false)
     * @param opts.includeAggregateErrors - whether to include explanations from aggregate errors (default: true)
     * @param opts.includeCause - whether to include the cause explanation (default: true)
     * @returns the user-facing error message
     */
    getExplanation(
      ctx: ErrorCtxType = {},
      {
        showErrorName = false,
        includeAggregateErrors = true,
      }: { showErrorName?: boolean; includeAggregateErrors?: boolean } = {}
    ): string {
      const allCtx = this.getMergedErrorContext(ctx);
      const explanation = punctuateSentenceWithPeriod(
        (this._getExplanation ? this._getExplanation(allCtx) : this.message) ||
          this.defaultExplanation
      );
      const causeExplanation =
        dedupeParagraphSentences(
          punctuateSentenceWithPeriod(this.getExplanationOfCause()),
          explanation
        ) || "";
      const explanationWithCause = `${explanation}${causeExplanation ? ` ${causeExplanation}` : ""}`;
      const aggregateErrorsExplanation = includeAggregateErrors
        ? dedupeParagraphSentences(
            punctuateSentenceWithPeriod(
              this.getExplanationOfAggregateErrors(allCtx)
            ),
            explanationWithCause
          ) || ""
        : "";

      return `${showErrorName ? `${this.nameWithCode}: ` : ""}${explanationWithCause}${aggregateErrorsExplanation ? ` ${aggregateErrorsExplanation}` : ""}`;
    }

    /**
     * Get the link the user can take to understand the error better, if
     * available.
     *
     * @param ctx - variables passed to the reference URL getter function
     * @returns the link the user can take to understand the error better, if
     * available
     */
    getReferenceURL(ctx: ErrorCtxType = {}): string {
      return this._getReferenceURL
        ? this._getReferenceURL(this.getMergedErrorContext(ctx))
        : "https://support.validere.com/";
    }

    /**
     * @internal
     */
    getMergedErrorContext(ctx: ErrorCtxType): ErrorCtxType {
      return {
        ...(this.errorCtx ?? {}),
        ...ctx,
      };
    }

    /**
     * Get the explanation of the cause. Note, the returned explanation may be punctuated with periods.
     * @internal
     */
    getExplanationOfCause(): string {
      if (!this._causeExplanation || typeof this._causeExplanation !== "string")
        return "";

      return this._causeExplanation;
    }

    /**
     * Get the explanation of the aggregate errors. Note, the returned explanation may be punctuated with periods.
     * @internal
     */
    getExplanationOfAggregateErrors(
      ctx: ErrorCtxType = {},
      opts: { showErrorName?: boolean } = {}
    ): string {
      return (
        this.errors
          ?.map((error) =>
            BaseError.normalize(error).getExplanation(
              this.getMergedErrorContext(ctx),
              opts
            )
          )
          .join(" ") ?? ""
      );
    }
  },
  plugins: [modernErrorsSerialize],
});
